import type { ReactNode } from "react"
import { cn } from "@/lib/utils"
import { SketchBox, Scribble } from "./index"

/**
 * An aside, in a drawn box, in someone's handwriting.
 *
 * This is the only place the app is allowed to be whimsical, and the constraint
 * is deliberate: a founder reading their own filing status or their own invoice
 * gets the product's real typeface, because handwriting on a number someone is
 * about to act on reads as unserious. A tip is not a number. A tip is the thing
 * a person leaning over your shoulder would say, so it looks like it.
 *
 * Use for guidance, empty states and pointers. Never for data, money, status,
 * dates, errors or anything a founder has to trust.
 */
export function Tip({
  children,
  seed,
  title,
  className,
  hand = false,
}: {
  children: ReactNode
  seed: string
  /** Optional short lead, e.g. "Worth knowing". */
  title?: string
  className?: string
  /** Render in the handwriting face. Off by default — guidance uses the product's own face. */
  hand?: boolean
}) {
  return (
    <SketchBox seed={seed} ink="pencil" width={1.6} className={cn("my-4", className)}>
      <div className="px-4 py-3">
        {title && (
          <p className={cn(hand ? "font-hand text-[17px]" : "text-sm", "leading-none text-primary")}>
            {title}
          </p>
        )}
        <div
          className={cn(
            hand ? "font-hand text-[16px]" : "text-sm",
            "leading-snug text-muted-foreground",
            title && "mt-1.5",
          )}
        >
          {children}
        </div>
      </div>
    </SketchBox>
  )
}

/** A one-line pointer with a little arrow, for sitting beside a control. */
export function Pointer({
  children,
  seed,
  point = "left",
  className,
}: {
  children: ReactNode
  seed: string
  point?: "left" | "right" | "up" | "down" | "none"
  className?: string
}) {
  return (
    <Scribble seed={seed} point={point} className={className}>
      {children}
    </Scribble>
  )
}
